import React, { useEffect, useMemo, useState } from "react";
import { adminApi } from "./api";
import { ChevronDown, ChevronRight, Sparkles, CalendarDays, Download, Lock, Mail } from "lucide-react";
import { formatSlot } from "../lib/booking";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString(undefined, {
  year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
}) : "—";
const fmtDay = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, {
  year: "numeric", month: "short", day: "numeric",
}) : "—";

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    adminApi.get("/clients")
      .then((r) => setClients(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return clients;
    return clients.filter((c) =>
      (c.name || "").toLowerCase().includes(s) ||
      (c.email || "").toLowerCase().includes(s)
    );
  }, [clients, q]);

  if (loading) return <div className="p-12 text-slate-500">Loading…</div>;

  return (
    <div className="p-10 max-w-7xl mx-auto space-y-8" data-testid="admin-clients">
      <header className="flex items-end justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-amber-700">clients</p>
          <h1 className="font-serif text-4xl text-slate-900 mt-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            All clients
          </h1>
          <p className="mt-1 text-sm text-slate-500">{filtered.length} {filtered.length === 1 ? "person" : "people"}</p>
        </div>
        <input
          data-testid="clients-search"
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or email..."
          className="px-4 py-2.5 rounded-full border border-slate-200 bg-white text-sm w-72 focus:outline-none focus:border-amber-500"
        />
      </header>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500">
          No clients yet.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="grid grid-cols-12 px-6 py-3 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-[0.22em] text-slate-500">
            <div className="col-span-4">Client</div>
            <div className="col-span-2">Blueprints</div>
            <div className="col-span-2">Sessions</div>
            <div className="col-span-2 text-right">Total paid</div>
            <div className="col-span-2 text-right">Last activity</div>
          </div>
          <div>
            {filtered.map((c, i) => {
              const isOpen = expanded === c.key;
              const paidBp = c.blueprints.filter((b) => b.unlocked).length;
              return (
                <div key={c.key} className="border-b border-slate-100 last:border-b-0">
                  <button
                    data-testid={`client-row-${i}`}
                    onClick={() => setExpanded(isOpen ? null : c.key)}
                    className="grid grid-cols-12 w-full px-6 py-4 items-center hover:bg-slate-50 text-left"
                  >
                    <div className="col-span-4 flex items-center gap-3">
                      <span className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 font-medium flex items-center justify-center text-sm shrink-0">
                        {(c.name || "?").trim().charAt(0).toUpperCase()}
                      </span>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate">{c.name || "—"}</p>
                        {c.email && <p className="text-xs text-slate-500 flex items-center gap-1 truncate"><Mail className="w-3 h-3" /> {c.email}</p>}
                      </div>
                    </div>
                    <div className="col-span-2 text-sm text-slate-700">
                      <span className="font-medium">{c.blueprints.length}</span>
                      {paidBp > 0 && <span className="ml-1.5 text-xs text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">{paidBp} paid</span>}
                    </div>
                    <div className="col-span-2 text-sm text-slate-700"><span className="font-medium">{c.bookings.length}</span></div>
                    <div className="col-span-2 text-right font-medium text-slate-900">${(c.total_paid_aud || 0).toLocaleString()} AUD</div>
                    <div className="col-span-2 text-right text-xs text-slate-500 flex items-center justify-end gap-1">
                      {fmtDay(c.last_seen)}
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-6 pb-6 pt-2 bg-slate-50/50 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid={`client-details-${i}`}>
                      {/* Blueprints */}
                      <div className="bg-white rounded-xl border border-slate-200 p-5">
                        <div className="flex items-center gap-2 text-amber-700 mb-3">
                          <Sparkles className="w-4 h-4" />
                          <p className="text-[10px] uppercase tracking-[0.22em]">Blueprints</p>
                        </div>
                        {c.blueprints.length === 0 ? (
                          <p className="text-sm text-slate-400">None.</p>
                        ) : (
                          <ul className="space-y-3">
                            {c.blueprints.map((b) => (
                              <li key={b.id} className="text-sm border-l-2 border-amber-200 pl-3">
                                <p className="text-slate-900 flex items-center gap-2">
                                  <span className="font-medium">{b.name}</span>
                                  {b.unlocked ? (
                                    <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">paid</span>
                                  ) : (
                                    <span className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded inline-flex items-center gap-1"><Lock className="w-2.5 h-2.5" />locked</span>
                                  )}
                                </p>
                                <p className="text-xs text-slate-500">Born {fmtDay(b.born)} · {b.place}</p>
                                <p className="text-xs text-slate-400 flex items-center gap-3 mt-1">
                                  <span>Generated {fmtDate(b.created_at)}</span>
                                  {b.unlocked && <span>· paid {fmtDate(b.unlocked_at)}</span>}
                                  {b.pdf_downloads > 0 && <span className="inline-flex items-center gap-1"><Download className="w-3 h-3" />{b.pdf_downloads}×</span>}
                                </p>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      {/* Bookings */}
                      <div className="bg-white rounded-xl border border-slate-200 p-5">
                        <div className="flex items-center gap-2 text-violet-700 mb-3">
                          <CalendarDays className="w-4 h-4" />
                          <p className="text-[10px] uppercase tracking-[0.22em]">Sessions</p>
                        </div>
                        {c.bookings.length === 0 ? (
                          <p className="text-sm text-slate-400">No bookings yet.</p>
                        ) : (
                          <ul className="space-y-3">
                            {c.bookings.map((b) => (
                              <li key={b.id} className="text-sm border-l-2 border-violet-200 pl-3">
                                <p className="text-slate-900 font-medium">{b.package_name}</p>
                                <p className="text-xs text-slate-500">
                                  {fmtDay(b.date)} · {b.time ? formatSlot(b.time) : "—"} · {b.format === "in_person" ? "In person" : "Online"}
                                </p>
                                <p className="text-xs text-slate-400">${b.amount_aud} AUD · {b.status}</p>
                                {b.intake_summary?.intentions?.some(Boolean) && (
                                  <details className="mt-2">
                                    <summary className="text-[11px] text-amber-700 cursor-pointer">Intake notes</summary>
                                    <ul className="mt-1.5 text-[11px] text-slate-600 space-y-1 list-disc pl-4">
                                      {b.intake_summary.intentions.filter(Boolean).map((q, i) => <li key={i}>{q}</li>)}
                                    </ul>
                                  </details>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
